import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { adminApi } from '@/api';
import {
  Alert,
  Badge,
  BoxAvatar,
  BoxList,
  BoxRow,
  Button,
  CardActions,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Icon,
  ListCard,
  LoadingState,
  Modal,
  PageHeader,
  Pagination,
  SearchInput,
  SegmentedTabs,
  Select,
  useToast,
} from '@/components/ui';
import { audienceLabel } from '@/features/billing/couponForm';
import { CouponFormModal } from '@/features/billing/CouponFormModal';
import { formatMoney } from '@/features/billing/money';
import { errorMessage } from '@/hooks/useApiError';
import { formatDate } from '@/utils/format';

/**
 * Coupons and promotions.
 *
 * Two things this screen refuses to do, both on purpose:
 *
 * **There is no delete.** A coupon that has been redeemed is part of why a
 * company was charged what it was, and a promotion that vanishes is a support
 * conversation with no answer. Retiring stops it being accepted and keeps the
 * record — the same treatment plans get.
 *
 * **A redeemed coupon's worth cannot be edited.** The server returns a 409 and
 * this screen says so before the admin tries: changing what a redeemed code is
 * worth would rewrite terms somebody already accepted.
 *
 * A coupon is for companies or for students, like a plan, and each row says
 * which.
 */

const TABS = [
  { key: 'live', label: 'Live', is_active: true },
  { key: 'retired', label: 'Retired', is_active: false },
  { key: 'all', label: 'All', is_active: undefined },
];

const PAGE_SIZE = 20;

/** What the coupon is worth, in one phrase. */
function worth(coupon) {
  if (coupon.discount_type === 'PERCENTAGE') return `${coupon.percent_off}% off`;
  return `${formatMoney(coupon.amount_off_cents, coupon.currency ?? 'EUR')} off`;
}

export function AdminCouponsPage() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [tab, setTab] = useState('live');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [audience, setAudience] = useState('');
  const [creating, setCreating] = useState(false);
  const [redemptionsFor, setRedemptionsFor] = useState(null);
  // The coupon waiting on "Retire it" -- the same confirmation plans and the
  // reference lists ask for before taking something out of use.
  const [retiring, setRetiring] = useState(null);
  const [editing, setEditing] = useState(null);

  const activeFilter = TABS.find((row) => row.key === tab)?.is_active;
  const params = {
    page,
    page_size: PAGE_SIZE,
    ...(activeFilter === undefined ? {} : { is_active: activeFilter }),
    ...(search.trim() ? { q: search.trim() } : {}),
    ...(audience ? { audience } : {}),
  };

  const { data, isPending, isPlaceholderData, isError, error, refetch } = useQuery({
    queryKey: ['admin-coupons', params],
    queryFn: () => adminApi.listCoupons(params),
    placeholderData: keepPreviousData,
  });

  const redemptions = useQuery({
    queryKey: ['admin-coupon-redemptions', redemptionsFor],
    queryFn: () => adminApi.listCouponRedemptions(redemptionsFor, { page_size: 50 }),
    enabled: Boolean(redemptionsFor),
  });

  function invalidate() {
    void queryClient.invalidateQueries({ queryKey: ['admin-coupons'] });
  }

  const setActive = useMutation({
    mutationFn: ({ code, is_active }) => adminApi.updateCoupon(code, { is_active }),
    onSuccess: (coupon) => {
      toast.success(coupon.is_active ? `${coupon.code} is live` : `${coupon.code} retired`);
      invalidate();
    },
    onError: (updateError) => toast.error(errorMessage(updateError, 'Could not update the coupon')),
  });

  const activeCount = (search !== '' ? 1 : 0) + (audience ? 1 : 0);

  function resetFilters() {
    setSearch('');
    setAudience('');
    setPage(1);
  }

  const rows = data?.data ?? [];
  const meta = data?.meta;

  return (
    <div>
      <PageHeader
        title="Coupons"
        description="A code comes off the invoice a plan change or a student upgrade produces. Nothing here can be deleted — retiring a coupon stops it being accepted and keeps the record of what people were charged."
        action={<Button onClick={() => setCreating(true)}>New coupon</Button>}
      />

      <ListCard
        isRefreshing={isPlaceholderData}
        toolbarStart={
          <SegmentedTabs
            variant="plain"
            tabs={TABS.map((row) => ({ key: row.key, label: row.label }))}
            value={tab}
            onChange={(key) => {
              setTab(key);
              setPage(1);
            }}
            label="Coupon status"
          />
        }
        toolbarEnd={
          <>
            <Select
              label="Who it is for"
              hideLabel
              size="sm"
              wrapperClassName="w-full sm:w-40"
              value={audience}
              onChange={(event) => {
                setAudience(event.target.value);
                setPage(1);
              }}
            >
              <option value="">Everyone</option>
              <option value="COMPANY">Companies</option>
              <option value="STUDENT">Students</option>
            </Select>
            <SearchInput
              label="Search coupons"
              hideLabel
              size="sm"
              className="w-full sm:w-64"
              placeholder="Code or description"
              value={search}
              onChange={(event) => {
                setSearch(event.target.value);
                setPage(1);
              }}
              onClear={() => {
                setSearch('');
                setPage(1);
              }}
            />
            {activeCount > 0 && (
              <Button variant="ghost" size="sm" onClick={resetFilters}>
                <Icon name="reset" size={14} />
                Reset
              </Button>
            )}
          </>
        }
        state={
          isError ? (
            <ErrorState message={errorMessage(error)} onRetry={() => void refetch()} />
          ) : isPending ? (
            <LoadingState label="Loading coupons…" />
          ) : rows.length === 0 ? (
            activeCount > 0 ? (
              <EmptyState
                title="Nothing matches these filters."
                description="No coupon matches that search and audience."
                action={
                  <Button variant="secondary" size="sm" onClick={resetFilters}>
                    Reset filters
                  </Button>
                }
              />
            ) : (
              <EmptyState
                title="No coupons"
                description="Create one and companies can apply it when they change plan, or students when they upgrade."
                action={<Button onClick={() => setCreating(true)}>New coupon</Button>}
              />
            )
          ) : null
        }
        footer={
          meta && (
            <Pagination
              className=""
              page={meta.page}
              pageSize={PAGE_SIZE}
              totalPages={meta.total_pages}
              totalItems={meta.total_items}
              onPageChange={setPage}
            />
          )
        }
      >
        <BoxList>
          {rows.map((row) => (
            <BoxRow
              key={row.id}
              accent={row.is_redeemable ? 'success' : 'neutral'}
              leading={<BoxAvatar icon="ticket" tone={row.is_redeemable ? 'brand' : 'neutral'} />}
              title={<span className="font-mono">{row.code}</span>}
              badges={
                <>
                  <Badge tone={row.audience === 'STUDENT' ? 'brand' : 'neutral'}>
                    {audienceLabel(row.audience)}
                  </Badge>
                  {row.is_redeemable ? (
                    <Badge dot tone="success">
                      Live
                    </Badge>
                  ) : (
                    <Badge dot tone="neutral">
                      {row.unavailable_reason}
                    </Badge>
                  )}
                </>
              }
              facts={[
                { icon: 'tag', tone: 'brand', text: worth(row) },
                {
                  icon: 'repeat',
                  text: row.duration === 'FOREVER' ? 'Every renewal' : 'First bill',
                },
                row.plan_codes?.length > 0 && {
                  icon: 'package',
                  text: `${row.plan_codes.join(', ')} only`,
                },
                row.expires_at && { icon: 'calendar', text: `Until ${formatDate(row.expires_at)}` },
                {
                  icon: 'users',
                  text: `Used ${row.times_redeemed}${row.max_redemptions ? ` / ${row.max_redemptions}` : ''}`,
                },
                {
                  icon: 'user',
                  text:
                    (row.max_uses_per_customer ?? 1) === 1
                      ? 'Once per customer'
                      : `${row.max_uses_per_customer}× per customer`,
                },
              ]}
              description={row.description}
              meta={
                <span className="text-ink-muted text-xs">Created {formatDate(row.created_at)}</span>
              }
              actions={
                <CardActions>
                  <Button
                    variant="secondary"
                    size="sm"
                    title={`Who used ${row.code}`}
                    disabled={row.times_redeemed === 0}
                    onClick={() => setRedemptionsFor(row.code)}
                  >
                    <Icon name="eye" size={14} />
                    Who used it
                  </Button>
                  <Button
                    variant="tintBrand"
                    size="sm"
                    title={`Edit ${row.code}`}
                    onClick={() => setEditing(row)}
                  >
                    <Icon name="pencil" size={14} />
                    Edit
                  </Button>
                </CardActions>
              }
              // Retiring stops the code being accepted, so it sits apart in the
              // corner; restoring a retired code takes the same place.
              corner={
                row.is_active ? (
                  <Button
                    variant="ghostDanger"
                    size="sm"
                    title={`Retire ${row.code}`}
                    onClick={() => setRetiring(row)}
                  >
                    <Icon name="archive" size={14} />
                    Retire
                  </Button>
                ) : (
                  <Button
                    variant="ghostPositive"
                    size="sm"
                    title={`Restore ${row.code}`}
                    isLoading={setActive.isPending && setActive.variables?.code === row.code}
                    onClick={() => setActive.mutate({ code: row.code, is_active: true })}
                  >
                    {!(setActive.isPending && setActive.variables?.code === row.code) && (
                      <Icon name="restore" size={14} />
                    )}
                    Restore
                  </Button>
                )
              }
            />
          ))}
        </BoxList>
      </ListCard>

      {editing && (
        <CouponFormModal key={editing.code} coupon={editing} onClose={() => setEditing(null)} />
      )}
      {creating && <CouponFormModal key="new" onClose={() => setCreating(false)} />}

      <ConfirmDialog
        open={retiring !== null}
        title={`Retire ${retiring?.code ?? ''}?`}
        message="Retiring stops the code being accepted. Nothing is deleted: every invoice it already discounted keeps its record, and you can restore it at any time."
        confirmLabel="Retire it"
        isLoading={setActive.isPending}
        onConfirm={() =>
          setActive.mutate(
            { code: retiring.code, is_active: false },
            { onSuccess: () => setRetiring(null) },
          )
        }
        onCancel={() => setRetiring(null)}
      />

      <Modal
        open={Boolean(redemptionsFor)}
        title={`Who used ${redemptionsFor}`}
        description="The count is a number; this is the answer to a dispute."
        onClose={() => setRedemptionsFor(null)}
      >
        {redemptions.isPending ? (
          <LoadingState label="Loading…" />
        ) : redemptions.isError ? (
          <Alert tone="error" title="Could not load who used this coupon">
            {errorMessage(redemptions.error)}
          </Alert>
        ) : (
          <ul className="divide-line divide-y text-sm">
            {(redemptions.data?.data ?? []).map((row) => (
              <li key={row.id} className="flex items-center justify-between gap-3 py-2">
                <span className="flex min-w-0 items-center gap-2">
                  <span className="text-ink truncate">{row.holder_name ?? row.company_name}</span>
                  <Badge tone={row.holder_type === 'STUDENT' ? 'brand' : 'neutral'}>
                    {row.holder_type === 'STUDENT' ? 'Student' : 'Company'}
                  </Badge>
                  {/* A student's code is only a claim until the invoice it discounts is paid. */}
                  {row.holder_type === 'STUDENT' && !row.student_subscription_id && (
                    <Badge tone="warning">Awaiting payment</Badge>
                  )}
                </span>
                <span className="text-ink-muted shrink-0 text-xs">
                  {formatDate(row.redeemed_at)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Modal>
    </div>
  );
}
